import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X } from 'lucide-react';
import { Button } from '@mbbe/ui';
import { helpContent, type PageHelpKey } from '../lib/helpContent';
import { hasSeenHelp, markHelpSeen } from '../lib/helpStorage';
import { useLanguage } from '../../contexts/LanguageContext';

export function DestekHelp({ pageKey }: { pageKey: PageHelpKey }) {
  const [open, setOpen] = useState(false);
  const { t, language } = useLanguage();
  const content = helpContent[language][pageKey];

  // Sayfa ilk kez açıldığında (tarayıcı bazlı) tanıtımı otomatik göster.
  useEffect(() => {
    if (!hasSeenHelp(pageKey)) {
      setOpen(true);
      markHelpSeen(pageKey);
    }
  }, [pageKey]);

  // Açıkken Esc ile kapat.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('destek_help_aria')}
        title={t('destek_help_title')}
        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      >
        <HelpCircle size={18} />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={content.title}
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-dark-900/95 shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-white/5 px-5 py-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500/15 text-brand-300">
                  <HelpCircle size={18} />
                </span>
                <div>
                  <h2 className="text-base font-semibold text-white">{content.title}</h2>
                  <p className="text-xs text-slate-400">{content.intro}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('destek_help_close_aria')}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                <X size={18} />
              </button>
            </div>

            <ul className="flex flex-col gap-3 px-5 py-4">
              {content.steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-xs font-semibold text-brand-300 tabular-nums">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-white">{step.title}</p>
                    <p className="text-xs text-slate-400">{step.body}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="border-t border-white/5 px-5 py-3 text-right">
              <Button variant="primary" size="sm" onClick={() => setOpen(false)}>
                {t('destek_help_got_it')}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
